import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Actions from '../flux/Actions';
import AuthStore from '../flux/stores/AuthStore';
import AuthBackground from './auth/AuthBackground';
import AuthLogo from './auth/AuthLogo';
import OfflineBanner from './auth/OfflineBanner';
import AuthForm from './auth/AuthForm';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5001';

function Auth() {
   const [isLogin, setIsLogin] = useState(true);
   const [email, setEmail] = useState('');
   const [password, setPassword] = useState('');
   const [confirmPassword, setConfirmPassword] = useState('');
   const [error, setError] = useState('');
   const [notice, setNotice] = useState('');
   const [isLoading, setIsLoading] = useState(false);
   const [isOnline, setIsOnline] = useState(navigator.onLine);

   useEffect(() => {
      const handleAuthChange = () => {
         setError(AuthStore.getError());
         setIsLoading(false);
      };

      const handleOnline = () => setIsOnline(true);
      const handleOffline = () => setIsOnline(false);

      AuthStore.addChangeListener(handleAuthChange);
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
         AuthStore.removeChangeListener(handleAuthChange);
         window.removeEventListener('online', handleOnline);
         window.removeEventListener('offline', handleOffline);
      };
   }, []);

   const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');
      setNotice('');

      if (!isOnline) {
         setError('Internet connection is required to use Quill Studio v1.');
         return;
      }

      setIsLoading(true);

      if (!isLogin) {
         if (password !== confirmPassword) {
            setError("Passwords don't match");
            setIsLoading(false);
            return;
         }

         try {
            const res = await fetch(`${API_BASE}/signup`, {
               method: 'POST',
               headers: { 'Content-Type': 'application/json' },
               body: JSON.stringify({ email, password }),
            });
            const data = await res.json().catch(() => ({}));

            if (!res.ok || data?.success === false) {
               setError(data?.message || `Signup failed (${res.status})`);
            } else {
               const status = data?.databaseStatus;
               const statusMessage = data?.databaseMessage;
               if (status && status !== 'ready') {
                  setNotice(
                     `Account created. Database setup is pending. ${
                        statusMessage ? statusMessage : 'You can still log in.'
                     }`,
                  );
               } else {
                  setNotice('Account created. You can now log in.');
               }

               setIsLogin(true);
               setPassword('');
               setConfirmPassword('');
            }
         } catch (err) {
            setError('Signup failed. Please try again.');
         }

         setIsLoading(false);
         return;
      }

      await Actions.login(email, password);
   };

   const toggleMode = () => {
      setIsLogin((prev) => !prev);
      setError('');
      setNotice('');
      setConfirmPassword('');
   };

   return (
      <div className="h-screen flex items-center justify-center bg-midnight">
         <AuthBackground />
         <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative z-10 w-full max-w-md p-8"
         >
            <AuthLogo />
            <OfflineBanner isOnline={isOnline} />
            <AuthForm
               isLogin={isLogin}
               email={email}
               password={password}
               confirmPassword={confirmPassword}
               error={error}
               notice={notice}
               isLoading={isLoading}
               onSubmit={handleSubmit}
               onEmailChange={(e) => setEmail(e.target.value)}
               onPasswordChange={(e) => setPassword(e.target.value)}
               onConfirmPasswordChange={(e) => setConfirmPassword(e.target.value)}
               onToggle={toggleMode}
            />
         </motion.div>
      </div>
   );
}

export default Auth;
